import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Coins, 
  Save, 
  RefreshCw, 
  Loader2, 
  Info,
  DollarSign,
  Zap, 
  FileText, 
  CheckCircle2, 
  AlertCircle
} from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Textarea } from "../../components/ui/textarea";

export default function PriceSettings() {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prices, setPrices] = useState<any>(null);
  
  // Bulk update state
  const [bulkText, setBulkText] = useState('');
  const [bulkPreview, setBulkPreview] = useState<any[]>([]);

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/settings/prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrices(response.data);
    } catch (error) {
      toast.error('Failed to fetch price configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/admin/settings/prices`, prices, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Price configuration updated successfully');
      setBulkText('');
      setBulkPreview([]);
    } catch (error) {
      toast.error('Failed to update price configuration');
    } finally {
      setSaving(false);
    }
  };

  const updatePrice = (category: string, model: string, field: string, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setPrices((prev: any) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [model]: {
          ...prev[category][model],
          [field]: numValue
        }
      }
    }));
  };

  const updateTwitterPrice = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setPrices((prev: any) => ({
      ...prev,
      TWITTERAPI_IO: {
        ...prev.TWITTERAPI_IO,
        credit: numValue
      }
    }));
  };

  // Parser for OpenAI pricing text
  const parseBulkText = (text: string) => {
    const lines = text.split('\n');
    const detected: any[] = [];
    const newOpenAiPrices = { ...prices.OPENAI };

    lines.forEach(line => {
      // Look for lines that start with a model name (gpt- or o1- or similar)
      // and have at least two dollar amounts
      const modelMatch = line.match(/^([a-z0-9\-\.]+)\s+/i);
      const dollarMatches = line.match(/\$(\d+\.?\d*)/g);

      if (modelMatch && dollarMatches && dollarMatches.length >= 2) {
        const modelName = modelMatch[1].toLowerCase();
        const inputPrice = parseFloat(dollarMatches[0].replace('$', ''));
        const outputPrice = parseFloat(dollarMatches[dollarMatches.length - 1].replace('$', ''));

        // We only care about models that start with gpt, o1, o3, etc. or are already in our list
        if (modelName.startsWith('gpt') || modelName.startsWith('o1') || modelName.startsWith('o3') || prices.OPENAI[modelName]) {
          detected.push({
            model: modelName,
            input: inputPrice,
            output: outputPrice,
            isNew: !prices.OPENAI[modelName]
          });
          
          newOpenAiPrices[modelName] = {
            input: inputPrice,
            output: outputPrice
          };
        }
      }
    });

    setBulkPreview(detected);
    if (detected.length > 0) {
      setPrices((prev: any) => ({
        ...prev,
        OPENAI: newOpenAiPrices
      }));
      toast.info(`Detected ${detected.length} models in the text!`);
    } else if (text.trim().length > 0) {
      toast.error('No models or prices detected. Check the format.');
    }
  };

  useEffect(() => {
    if (bulkText.trim().length > 0) {
      const timer = setTimeout(() => parseBulkText(bulkText), 500);
      return () => clearTimeout(timer);
    } else {
      setBulkPreview([]);
    }
  }, [bulkText]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-20 px-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-foreground flex items-center gap-3 italic">
            <Coins className="h-8 w-8 text-yellow-500 not-italic" />
            Price Settings
          </h1>
          <p className="text-muted-foreground font-medium italic">
            Manage API costs and bulk update from OpenAI pricing pages.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={fetchPrices} 
            variant="outline" 
            className="border-border text-muted-foreground hover:text-foreground hover:bg-card font-black text-[10px] tracking-widest uppercase"
          >
            <RefreshCw className="h-3 w-3 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-primary hover:bg-primary/90 text-white font-black px-6 shadow-lg shadow-primary/20"
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            SAVE ALL CHANGES
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Editor & Bulk Update (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Bulk Update Card */}
          <Card className="bg-card border-border shadow-2xl overflow-hidden border-t-4 border-t-primary">
            <CardHeader className="bg-muted/50 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-black text-foreground italic">OpenAI Bulk Updater</CardTitle>
                  <CardDescription className="text-muted-foreground font-medium uppercase text-[10px] tracking-widest">
                    Paste OpenAI pricing text here to auto-update
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <Textarea 
                placeholder="Paste pricing table content here (e.g. gpt-4o $2.50 $1.25 $10.00)..."
                className="min-h-[200px] bg-muted border-border font-mono text-xs text-muted-foreground focus:border-primary/50 focus:ring-primary/20 transition-all"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
              
              {bulkPreview.length > 0 && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3" />
                    Detected Updates ({bulkPreview.length} models)
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {bulkPreview.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-primary/5 border border-primary/10 text-[10px] font-bold">
                        <span className="text-foreground font-mono">{p.model}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground">In: <span className="text-primary">${p.input}</span></span>
                          <span className="text-muted-foreground">Out: <span className="text-primary">${p.output}</span></span>
                          {p.isNew && <Badge className="bg-primary h-4 text-[8px] font-black uppercase">NEW</Badge>}
                        </div>
                      </div>
                    ))}
                    {bulkPreview.length > 5 && (
                      <p className="text-[9px] text-muted-foreground font-black italic text-center">... and {bulkPreview.length - 5} more models updated in the fields below</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* OpenAI Models Detail */}
          <Card className="bg-card border-border shadow-xl overflow-hidden">
            <CardHeader className="bg-muted/50 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Zap className="h-5 w-5 text-purple-500" />
                </div>
                <CardTitle className="text-xl font-black text-foreground italic">Manual Adjustment</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {Object.entries(prices.OPENAI).map(([model, config]: [string, any]) => (
                <div key={model} className="space-y-3 p-4 rounded-xl bg-muted/40 border border-border hover:border-border transition-all">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="bg-muted text-foreground border-border font-mono text-[10px] uppercase font-black px-2">
                      {model}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase text-muted-foreground tracking-tighter">Input / 1M</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input 
                          type="number" 
                          step="0.01"
                          value={config.input}
                          onChange={(e) => updatePrice('OPENAI', model, 'input', e.target.value)}
                          className="h-8 bg-background border-border pl-8 font-mono text-xs font-bold text-foreground"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase text-muted-foreground tracking-tighter">Output / 1M</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input 
                          type="number" 
                          step="0.01"
                          value={config.output}
                          onChange={(e) => updatePrice('OPENAI', model, 'output', e.target.value)}
                          className="h-8 bg-background border-border pl-8 font-mono text-xs font-bold text-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Twitter & Info (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
        {/* twitterapi.io Section */}
        <Card className="bg-card border-border shadow-xl overflow-hidden border-t-4 border-t-primary">
          <CardHeader className="bg-muted/50 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-primary">
                  <path d="M18.244 2.25h3.308l-7.227 3.779 8.502 11.221h-6.657l-5.214-6.817-5.966 6.817H1.676l7.728-8.831L1.242 2.25h6.826l4.704 6.231 5.472-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                </svg>
              </div>
              <CardTitle className="text-lg font-black text-foreground italic">twitterapi.io Credits</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Price per 1 Credit ($)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  type="number" 
                  step="0.000001"
                  value={prices.TWITTERAPI_IO.credit}
                  onChange={(e) => updateTwitterPrice(e.target.value)}
                  className="bg-muted border-border pl-10 font-mono font-bold text-foreground"
                />
              </div>
              <div className="p-3 bg-primary/5 rounded-lg border border-primary/10 mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-black uppercase text-muted-foreground tracking-tighter">Conversion:</span>
                  <span className="text-[10px] font-black text-primary font-mono italic">100,000 cr = $1.00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-black uppercase text-muted-foreground tracking-tighter">Min call (15 cr):</span>
                  <span className="text-[10px] font-black text-primary font-mono">${(prices.TWITTERAPI_IO.credit * 15).toFixed(5)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Credit Reference</h4>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex justify-between p-2 rounded bg-muted/50 border border-border text-[10px]">
                  <span className="text-muted-foreground">Tweets</span>
                  <span className="text-foreground font-bold">15 cr / tweet</span>
                </div>
                <div className="flex justify-between p-2 rounded bg-muted/50 border border-border text-[10px]">
                  <span className="text-muted-foreground">Profiles</span>
                  <span className="text-foreground font-bold">18 cr / profile</span>
                </div>
                <div className="flex justify-between p-2 rounded bg-muted/50 border border-border text-[10px]">
                  <span className="text-muted-foreground">List calls</span>
                  <span className="text-foreground font-bold">150 cr / call</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

          {/* Reference Card */}
          <Card className="bg-muted/50 border-border shadow-xl border-dashed">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-black text-muted-foreground uppercase tracking-widest italic">Official Prices (Ref)</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center text-[10px] font-bold">
                <span className="text-muted-foreground uppercase tracking-tighter">gpt-4o Input:</span>
                <span className="text-foreground font-mono">$2.50 / 1M</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-bold">
                <span className="text-muted-foreground uppercase tracking-tighter">gpt-4o Output:</span>
                <span className="text-foreground font-mono">$10.00 / 1M</span>
              </div>
              <Separator className="bg-card" />
              <div className="flex justify-between items-center text-[10px] font-bold">
                <span className="text-muted-foreground uppercase tracking-tighter">gpt-4o-mini Input:</span>
                <span className="text-foreground font-mono">$0.15 / 1M</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-bold">
                <span className="text-muted-foreground uppercase tracking-tighter">gpt-4o-mini Output:</span>
                <span className="text-foreground font-mono">$0.60 / 1M</span>
              </div>
              
              <Alert className="mt-4 bg-yellow-500/5 border-yellow-500/20 text-yellow-500 p-3">
                <AlertCircle className="h-3.5 w-3.5" />
                <AlertDescription className="text-[9px] font-bold leading-tight">
                  Updates to prices only affect calculations for NEW calls. Historical consumption costs remain unchanged.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
